import {FileUploadWithModal} from '@/components/organisms';
import PropTypes from 'prop-types';
import {Constants} from '@/utils/constants/commonConstants';
import {useIntl} from 'react-intl';
import {convertCSV} from '@/helpers/convertCSV';
import {postUploadStores} from '@/services/ClubPlus/TierService';
import {setToastNotifications} from '@/store/slices/commonSlice';
import {useAppDispatch} from '@/store';
import {useMutation} from '@tanstack/react-query';
import {useState, useEffect, useRef} from 'react';

const progressInterval = 200;
const simulatedProgressMax = 90;
const simulatedProgressIncrement = 5;

export const TierFileUploadModal = ({
  isVisible,
  setIsVisible,
  onSuccess,
  setFormData,
  formData,
  tierID,
  tierName
}) => {
  const {formatMessage} = useIntl();
  const dispatch = useAppDispatch();
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [parsedStoreIds, setParsedStoreIds] = useState([]);
  const progressIntervalRef = useRef(null);

  const {mutateAsync, isPending} = useMutation({
    mutationKey: ['postUploadStores'],
    mutationFn: (payload) => postUploadStores(payload)
  });

  useEffect(() => {
    if (!isVisible) {
      setUploadProgress(0);
      setUploading(false);
      setParsedStoreIds([]);
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
        progressIntervalRef.current = null;
      }
    }
  }, [isVisible]);

  useEffect(() => {
    return () => {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
        progressIntervalRef.current = null;
      }
    };
  }, []);

  const closeModal = () => {
    setIsVisible(false);
    setFormData({});
    setUploadProgress(0);
    setUploading(false);
    setParsedStoreIds([]);
  };

  const simulateProgress = () => {
    setUploadProgress(0);
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
    }
    const interval = setInterval(() => {
      setUploadProgress((currentProgress) => {
        const newProgress = currentProgress + simulatedProgressIncrement;
        if (newProgress >= simulatedProgressMax) {
          clearInterval(interval);
          progressIntervalRef.current = null;
          return simulatedProgressMax;
        }
        return newProgress;
      });
    }, progressInterval);
    progressIntervalRef.current = interval;

    return () => {
      clearInterval(interval);
      progressIntervalRef.current = null;
    };
  };

  const handleUploadStores = async () => {
    const payload = {
      tierId: tierID,
      storeIds: parsedStoreIds
    };

    setUploading(true);
    const stopProgress = simulateProgress();

    try {
      const response = await mutateAsync(payload);

      setUploadProgress(100);

      if (response?.unprocessedStoreIds?.length === 0) {
        closeModal();
        onSuccess();
        dispatch(
          setToastNotifications({
            show: true,
            message: formatMessage({
              id: 'CLUB_PLUS.TIERS_ADD_STORES_UPLOAD_SUCCESS'
            }),
            type: 'success'
          })
        );
      } else if (response?.unprocessedStoreIds === null) {
        closeModal();
        dispatch(
          setToastNotifications({
            show: true,
            message: formatMessage(
              {id: 'CLUB_PLUS.TIER_FILE_UPLOAD_MODAL_DIALOG_ERROR'},
              {br: <br />}
            ),
            type: 'error'
          })
        );
      } else {
        const conflictingStoresNumber =
          response?.unprocessedStoreIds?.length || 0;
        const totalStoresNumber = parsedStoreIds.length;
        const successStoresNumber = totalStoresNumber - conflictingStoresNumber;

        closeModal();
        dispatch(
          setToastNotifications({
            show: true,
            message: formatMessage(
              {id: 'CLUB_PLUS.TIER_FILE_UPLOAD_MODAL_DIALOG_WARNING'},
              {
                tierName,
                conflictingStoresNumber,
                totalStoresNumber,
                successStoresNumber
              }
            ),
            type: 'warning'
          })
        );
      }
    } catch (err) {
      setUploadProgress(0);
      stopProgress();
      setUploading(false);

      const backendData = err?.data?.data || err?.response?.data?.data;

      if (
        backendData?.invalidStoreIds &&
        Array.isArray(backendData.invalidStoreIds)
      ) {
        const invalidStoreIds = backendData.invalidStoreIds
          .map((item) => item?.value)
          .filter((id) => id && id.trim());

        if (invalidStoreIds.length > 0) {
          const maxToShow = 10;
          const idsToShow = backendData.invalidStoreIds.slice(0, maxToShow);
          const remainingCount = backendData.invalidStoreIds.length - maxToShow;

          const reasonMessages = {
            contains_spaces: 'contains spaces',
            contains_letters: 'contains letters',
            special_characters: 'contains special characters',
            invalid_format: 'invalid format'
          };

          const listItems = idsToShow.map((item) => {
            const reason = reasonMessages[item.reason] || item.reason;
            return `• ${item.value} (${reason})`;
          });

          if (remainingCount > 0) {
            listItems.push(`• ... and ${remainingCount} more`);
          }

          const invalidStoreIdsList = listItems.join('\n');

          const errorMessage = `Invalid Store IDs detected. Found ${backendData.totalInvalid || invalidStoreIds.length} invalid Store${(backendData.totalInvalid || invalidStoreIds.length) > 1 ? ' IDs' : ' ID'}:\n\n${invalidStoreIdsList}\n\nStore IDs must be numeric only (e.g., 123, 456789). Please fix the issues above and try again.`;

          setFormData({
            feedbackType: 'error',
            helperText: errorMessage
          });
        }
      } else {
        const errMsg =
          err?.response?.data?.message ||
          err?.data?.message ||
          err?.message?.message ||
          err?.cause ||
          err?.message ||
          formatMessage({id: 'GENERAL.UNKNOWN_ERROR'});
        dispatch(
          setToastNotifications({
            show: true,
            message: errMsg,
            type: 'error'
          })
        );
        closeModal();
      }
    }
  };

  const handleDeleteFile = () => {
    setParsedStoreIds([]);
    setFormData({});
  };

  return (
    <FileUploadWithModal
      openUpload={isVisible}
      setOpenUpload={closeModal}
      formData={formData}
      setFormData={setFormData}
      title={formatMessage({
        id: 'CLUB_PLUS.TIER_FILE_UPLOAD_MODAL_DIALOG_TITLE'
      })}
      label={formatMessage(
        {id: 'CLUB_PLUS.TIER_FILE_UPLOAD_MODAL_DIALOG_LABEL'},
        {tierName}
      )}
      uploadFor={Constants.RewardsTierStoreUpload}
      uploading={uploading}
      setUploading={setUploading}
      uploadProgress={uploadProgress}
      customUploadFile={async (file, handleFileError) => {
        const reader = new FileReader();
        reader.onload = async () => {
          try {
            const csvText = reader.result;
            const convertedCSV = convertCSV(csvText);
            const newStoreIDs = convertedCSV
              .map((item) => item['Store Id'])
              .filter((id) => id && id.trim() !== '');

            if (newStoreIDs.length === 0) {
              handleFileError({
                feedbackType: 'error',
                helperText: formatMessage({
                  id: 'CLUB_PLUS.TIER_FILE_UPLOAD_EMPTY_CSV'
                })
              });
              setFormData({});
              return;
            }

            const uniqueStoreIds = [...new Set(newStoreIDs)];
            if (uniqueStoreIds.length !== newStoreIDs.length) {
              handleFileError({
                feedbackType: 'warning',
                helperText: formatMessage({
                  id: 'CLUB_PLUS.TIER_FILE_UPLOAD_MODAL_DIALOG_DUPLICATE_STORE_IDS'
                })
              });
              setFormData({});
              return;
            }

            setParsedStoreIds(uniqueStoreIds);
          } catch {
            handleFileError({
              feedbackType: 'error',
              helperText: formatMessage({
                id: 'CLUB_PLUS.TIER_FILE_UPLOAD_INVALID_CSV'
              })
            });
            setFormData({});
          }
        };

        reader.readAsText(file);
      }}
      onClickPrimary={handleUploadStores}
      onDelete={handleDeleteFile}
      primaryButtonDisabled={isPending || parsedStoreIds.length === 0}
      primaryButtonText={`Upload ${parsedStoreIds.length} store${parsedStoreIds.length !== 1 ? 's' : ''}`}
      required={false}
    />
  );
};

TierFileUploadModal.propTypes = {
  isVisible: PropTypes.bool.isRequired,
  setIsVisible: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
  setFormData: PropTypes.func.isRequired,
  formData: PropTypes.object.isRequired,
  tierID: PropTypes.string.isRequired,
  tierName: PropTypes.string.isRequired
};
